import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, Building2, GraduationCap, FileText, Sparkles,
  Compass, Bell, LogOut, ChevronRight, Menu, X, Wallet, QrCode, KeyRound, Megaphone, BarChart3, Award, PenLine, Globe,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LogoMark } from "@/components/brand/Logo";
import ChangePasswordDialog from "@/components/account/ChangePasswordDialog";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/leads", label: "Leads & Pipeline", icon: Users, testid: "nav-leads" },
  { to: "/app/clients", label: "Clients", icon: Building2, testid: "nav-clients" },
  { to: "/app/workshops", label: "Training", icon: GraduationCap, testid: "nav-workshops" },
  { to: "/app/facilitator", label: "Facilitator", icon: QrCode, testid: "nav-facilitator" },
  { to: "/app/proposals", label: "Proposals", icon: FileText, testid: "nav-proposals" },
  { to: "/app/finance", label: "Finance", icon: Wallet, testid: "nav-finance" },
  { to: "/app/ccs-framework", label: "CCS Framework", icon: Compass, testid: "nav-ccs" },
  { to: "/app/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing" },
  { to: "/app/spotlight", label: "Spotlight", icon: Award, testid: "nav-spotlight" },
  { to: "/app/insights", label: "Insights", icon: PenLine, testid: "nav-insights" },
  { to: "/app/site-content", label: "Website Content", icon: Globe, testid: "nav-site-content" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai" },
];

const SidebarContent = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const [changePwOpen, setChangePwOpen] = useState(false);
  return (
    <div className="h-full flex flex-col" style={{ background: "var(--ccs-sidebar-bg)" }}>
      {/* Brand */}
      <div className="px-6 pt-7 pb-6">
        <Link to="/app/dashboard" data-testid="brand-link" className="flex items-center gap-3 group">
          <LogoMark size={42} testid="sidebar-logo" />
          <div className="leading-tight">
            <div className="font-display text-white font-semibold text-base tracking-tight">The CCS Approach</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F0E6D2]/60 font-medium">Communicate · Collaborate · Serve</div>
          </div>
        </Link>
        <div className="mt-5 text-xs text-[#F0E6D2]/55 font-medium leading-relaxed">
          Joylance Chapman-George
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `ccs-nav-item flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium ${
                isActive
                  ? "bg-[#5D3A1F] text-white shadow-[0_4px_16px_rgba(45,90,76,0.35)]"
                  : "text-[#F0E6D2]/75 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-white/10 mx-3 mb-3 mt-3" />
      <div className="px-4 pb-5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition"
            >
              <Avatar className="w-9 h-9">
                <AvatarFallback className="bg-[#C28A2E] text-white text-sm font-semibold">
                  {user?.name?.[0]?.toUpperCase() || "C"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm text-white font-medium truncate">{user?.name || "User"}</div>
                <div className="text-[11px] text-[#F0E6D2]/55 truncate">{user?.email}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#F0E6D2]/55" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setChangePwOpen(true)} data-testid="change-password-menu-item">
              <KeyRound className="w-4 h-4 mr-2" /> Change password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-[#A23E3E] focus:text-[#A23E3E]">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
};

const NotificationBell = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/notifications").then((r) => setCount(r.data.count || 0)).catch(() => {});
    const t = setInterval(() => {
      api.get("/notifications").then((r) => setCount(r.data.count || 0)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <NavLink
      to="/app/notifications"
      data-testid="notifications-bell"
      className="relative p-2.5 rounded-xl border border-[#EBE3D2] hover:border-[#5D3A1F]/40 hover:bg-white transition"
    >
      <Bell className="w-[18px] h-[18px] text-[#2A1810]" strokeWidth={1.7} />
      {count > 0 && (
        <Badge
          data-testid="notifications-count"
          className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1.5 rounded-full bg-[#C28A2E] text-white text-[10px] font-bold border-2 border-[#FDFBF5]"
        >
          {count}
        </Badge>
      )}
    </NavLink>
  );
};

const AppShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#FDFBF5]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 max-w-[85%]"><SidebarContent onNavigate={() => setMobileOpen(false)} /></div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header
          className="sticky top-0 z-30 backdrop-blur-md bg-[#FDFBF5]/85 border-b border-[#EBE3D2]"
          data-testid="app-header"
        >
          <div className="flex items-center justify-between px-5 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-[#EBE3D2]"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="hidden md:block">
                <LogoMark size={36} testid="header-logo" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">The CCS Approach · Business OS</div>
                <div className="font-display text-lg text-[#2A1810] font-semibold tracking-tight">
                  Welcome back, Joylance
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-5 lg:p-8 max-w-[1400px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
